import React from 'react'
import PropTypes from 'prop-types'
import Turbolinks from 'turbolinks'
import { Drawer, Disclosure, DescriptionList, Media, Image, Text, Price, Level, Button, Clipped, Select, Box, Toasts, Placeholder, ContentFallback, Link, Conditional, Title } from '@deseretbook/react-ui'
import { Analytics, Logger, formatErrorMessage } from '../../dbcom'
import { DeseretBook } from '../../dbcom/api'

const QUANTITY_OPTIONS = [...Array(100).keys()].splice(1)

const logger = new Logger({ context: 'CartSidebar' })

/**
 * User's cart summary in sidebar form. This component displays a summary of the user's current
 * order. You manage when the sidebar is open/closed with the `isOpen` and `onClose` props. You
 * need to pass in the `orderNumber` and `orderToken` props so it knows what order to fetch.
 *
 * There are also two modes available, editing and read only. By default users are able to remove
 * any items from their cart, or update their quantity. In some situations this is un-desireable,
 * so you can set `isReadOnly` to disable that functionality.
 *
 * If you aren't using `isReadOnly`, there's a `onUpdate` callback you might find useful as any time
 * an updated version of the order is retrieved the `onUpdate` callback is called and passed the new
 * version of the order. This is useful if you're showing some info about the order, like the cart
 * count in the button that opens/closes the cart sidebar for instance.
 *
 * That's pretty much everything you need to know. 💜
 *
 * @returns {function} Component
 */
const CartSidebar = ({
  orderNumber,
  orderToken,
  popupProps,
  onClose,
  onUpdate,
  isReadOnly,
  ...rest
}) => {
  const [isInitialized, setInitialized] = React.useState(false)
  const [isFetchingOrder, setFetchingOrder] = React.useState(false)
  const [timesOpened, setTimesOpened] = React.useState(0)
  const [isRedirecting, setRedirecting] = React.useState(false)
  const [order, setOrder] = React.useState()
  const [isRemovingLineItem, setRemovingLineItem] = React.useState(false)
  const [updatingQuantity, setUpdatingQuantity] = React.useState(false)
  const personalizationDisplayTypes = { 'Spree::Personalization::Imprint': 'Imprint Personalization', 'Spree::Personalization::Art': 'Art Personalization' }

  const { isOpen } = popupProps

  const isLoading = !!(
    !isInitialized || isFetchingOrder || isRemovingLineItem || updatingQuantity || isRedirecting
  )

  /**
   * Updates the stored order and calls any relevant callbacks.
   *
   * @param {object} updatedOrder - Newest order info.
   */
  const updateOrder = (updatedOrder) => {
    setOrder(updatedOrder)

    if (typeof onUpdate === 'function') {
      onUpdate(updatedOrder)
    }
  }

  /**
   * Gets the order and initializes things the cart needs.
   */
  const initialize = async () => {
    if (!orderNumber || !orderToken) {
      Analytics
        .event({
          category: 'Cart Summary',
          action: 'Cart Contents Retrieved',
          label: !orderNumber
            ? 'Missing Order Number'
            : 'Missing Order Token',
        }, { isInteractive: false })

      setInitialized(true)

      return
    }

    try {
      setFetchingOrder(true)
      const currentOrder = await DeseretBook
        .order({ number: orderNumber, token: orderToken })
        .get()

      updateOrder(currentOrder)
      setFetchingOrder(false)
      setInitialized(true)

      Analytics
        .event({
          category: 'Cart Summary',
          action: 'Cart Contents Retrieved',
          label: `Order Quantity: ${currentOrder.totalQuantity}`,
        }, { isInteractive: false })
    } catch ({ message, errors }) {
      setFetchingOrder(false)
      logger.error(`Unable to fetch cart summary: "${formatErrorMessage(message, errors)}"`, { errors, orderNumber, orderTokenPresent: !!orderToken })
    }
  }

  /**
   * Removes the line item.
   * @param {object} lineItem - Line item to remove.
   */
  const removeLineItem = async (lineItem) => {
    if (isReadOnly) {
      return
    }

    try {
      setRemovingLineItem(lineItem.id)

      await DeseretBook
        .checkout({ ...order, token: orderToken })
        .lineItem(lineItem)
        .delete(lineItem)

      // Since the line item update doesn't return the updated order, we'll have to go get the
      // updated version.
      const currentOrder = await DeseretBook
        .order({ number: orderNumber, token: orderToken })
        .get()

      updateOrder(currentOrder)
      setRemovingLineItem(false)

      Toasts.success('Item removed from cart')

      // Track the user's action
      Analytics
        .event({
          category: 'Cart Summary',
          action: 'Removed Line Item',
          label: lineItem.variant.name,
        }, { isInteractive: true })
    } catch ({ message, errors }) {
      setRemovingLineItem(false)
      Toasts.danger('Failed to remove item', formatErrorMessage(message, errors))
      logger.critical(`Unable to remove line item: "${formatErrorMessage(message, errors)}"`, { errors, lineItem, order })

      // Track the user's action
      Analytics
        .event({
          category: 'Cart Summary',
          action: 'Failed to Remove Line Item',
          label: message,
        }, { isInteractive: true })
    }
  }

  /**
   * Updates the line item's quantity.
   * @param {object} lineItem - Line item to update
   * @param {number} quantity - New line item quantity
   */
  const updateQuantity = async (lineItem, quantity) => {
    if (isReadOnly) {
      return
    }

    try {
      setUpdatingQuantity(lineItem.id)

      await DeseretBook
        .checkout({ ...order, token: orderToken })
        .lineItem(lineItem)
        .update({ ...lineItem, quantity })

      // Since the line item update doesn't return the updated order, we'll have to go get the
      // updated version.
      const currentOrder = await DeseretBook
        .order({ number: orderNumber, token: orderToken })
        .get()

      updateOrder(currentOrder)
      setUpdatingQuantity(false)

      Toasts.success('Quantity updated')

      // Track the user's action
      Analytics
        .event({
          category: 'Cart Summary',
          action: 'Updated Line Item Quantity',
          label: `${lineItem.quantity} → ${quantity}`,
        }, { isInteractive: true })
    } catch ({ message, errors }) {
      setUpdatingQuantity(false)
      logger.critical(`Failed to update line item quantity: "${formatErrorMessage(message, errors)}"`, {
        errors,
        lineItem,
        quantity,
        order,
      })
      Toasts.danger('Failed to update quantity', formatErrorMessage(message, errors))

      // Track the error in GA
      Analytics
        .event({
          category: 'Cart Summary',
          action: 'Failed to Update Line Item Quantity',
          label: message,
        }, { isInteractive: true })
    }
  }

  // Fetch the order once the sidebar is opened for the first time.
  React.useEffect(() => {
    if (!order && isOpen && !isFetchingOrder && !isInitialized) {
      initialize()
    }

    if (isInitialized || isOpen) {
      // Track the open/close event
      Analytics
        .event({
          category: 'Cart Summary',
          action: isOpen
            ? 'User Opened Cart Summary'
            : 'User Closed Cart Summary',
          label: isOpen && `Times Opened: ${timesOpened + 1}`,
        }, { isInteractive: true })
    }

    // Update open count so we can track the number of times the user opens the cart per page.
    if (isOpen) {
      setTimesOpened(timesOpened + 1)
    }
  }, [isOpen])

  return (
    <Drawer position="right" {...popupProps} close={onClose} {...rest}>
      <Drawer.Header>
        <Title
          aria-level="6"
          alignment="center"
          isPaddingless
          title="Your Cart"
          subtitle={`${(order && order.totalQuantity) || 0} Item${
            (!order || order.totalQuantity !== 1)
              ? 's'
              : ''
          }`}
        />
      </Drawer.Header>
      {!!(isFetchingOrder || !isInitialized) && (
        <Drawer.Content>
          <Placeholder>
            {[1, 2, 3, 4].map(key => (
              <Media key={key}>
                <Media.Figure>
                  <Placeholder.Block width={3} height={4} />
                </Media.Figure>
                <Media.Content>
                  <Placeholder.Text lines={1} />
                  <Box width={5}>
                    <Placeholder.Text lines={1} size="tiny" />
                  </Box>
                  <Box paddingVertical={1}>
                    <Text><Price /></Text>
                  </Box>
                </Media.Content>
              </Media>
            ))}
          </Placeholder>
        </Drawer.Content>
      )}
      {!!(!isFetchingOrder && isInitialized) && (
        <Drawer.Content>
          <ContentFallback
            title="Your Cart is Empty"
            subtitle="Looking for a gift?"
            actionContent={(
              <Button
                type="button"
                icon="shoppingCart"
                color="action"
                isLoading={isRedirecting}
                onClick={() => {
                  if (window.location.pathname === '/t/gift-ideas') {
                    Analytics
                      .event({
                        category: 'Cart Summary',
                        action: 'Empty Cart Action Button Clicked',
                      }, {
                        isInteractive: true,
                        callback: () => {
                          onClose()
                        }
                      })
                  } else {
                    setRedirecting(true)

                    Analytics
                      .event({
                        category: 'Cart Summary',
                        action: 'Empty Cart Action Button Clicked',
                      }, {
                        isInteractive: true,
                        callback: () => {
                          // We'll wait for the event to track before we actually redirect the user.
                          Turbolinks.visit('/t/gift-ideas')
                          onClose()
                        },
                      })
                  }
                }}
              >
                See Gift Ideas
              </Button>
            )}
            imageProps={{
              src: '//deseretbook.s3.amazonaws.com/images/content_fallback/cart.svg',
              'aria-hidden': true,
              imageHeight: 857.08,
              imageWidth: 1116.21,
            }}
          >
            {order && order.lineItems && order.lineItems.map(lineItem => (
              <Media key={lineItem.id}>
                {lineItem.variant.images.length && (
                  <Media.Figure size="small">
                    <Conditional
                      condition={!isReadOnly && !!lineItem.variant.slug}
                      component={Link}
                      componentProps={{
                        href: `/p/${lineItem.variant.slug}?navigation_source=cartName`,
                        onClick: (e) => {
                          e.preventDefault()

                          Analytics
                            .event({
                              category: 'Cart Summary',
                              action: 'Product Image Click',
                              label: lineItem.variant.name,
                            }, {
                              isInteractive: true,
                              callback: () => {
                                // We'll wait for the event to track before we actually redirect the user.
                                Turbolinks.visit(`/p/${lineItem.variant.slug}?navigation_source=cartName`)
                              },
                            })
                        },
                        style: {
                          display: 'block',
                          width: '100%',
                        },
                      }}
                    >
                      <Image
                        src={lineItem.variant.images[0].smallUrl}
                        alt={lineItem.variant.images[0].alt || lineItem.variant.name}
                        imageWidth={lineItem.variant.images[0].attachmentWidth}
                        imageHeight={lineItem.variant.images[0].attachmentHeight}
                      />
                    </Conditional>
                  </Media.Figure>
                )}
                <Media.Content style={{ minWidth: 0 }}>
                  <Level>
                    <Level.Left isShrinkable>
                      <Level.Item>
                        <Clipped>
                          <Conditional
                            condition={!isReadOnly && !!lineItem.variant.slug}
                            component={Link}
                            componentProps={{
                              href: `/p/${lineItem.variant.slug}?navigation_source=cartImage`,
                              onClick: (e) => {
                                e.preventDefault()

                                Analytics
                                  .event({
                                    category: 'Cart Summary',
                                    action: 'Product Name Click',
                                    label: lineItem.variant.name,
                                  }, {
                                    isInteractive: true,
                                    callback: () => {
                                      Turbolinks.visit(`/p/${lineItem.variant.slug}?navigation_source=cartImage`)
                                    },
                                  })
                              },
                            }}
                          >
                            <Text hasEllipsis>{lineItem.variant.name}</Text>
                          </Conditional>
                          <Text size="tiny" color="muted" hasEllipsis>
                            {lineItem.variant.optionValues.map(({ presentation }) => presentation).join(' ')}
                          </Text>
                          {/* Personalizations */}
                          {lineItem.personalization && (
                            <Disclosure>
                              <Disclosure.Header>
                                {personalizationDisplayTypes[lineItem.personalization.type] || 'Personalization'}
                              </Disclosure.Header>
                              <Disclosure.Content>
                                  <DescriptionList>
                                    {lineItem.personalization.lines.map(line => (
                                      <React.Fragment>
                                        <DescriptionList.Term>
                                          {line.title}
                                        </DescriptionList.Term>
                                        <DescriptionList.Description>
                                          {line.value}
                                        </DescriptionList.Description>
                                      </React.Fragment>
                                    ))}
                                    {!!lineItem.personalization.style && (
                                      <React.Fragment>
                                        <DescriptionList.Term>
                                          Style
                                        </DescriptionList.Term>
                                        <DescriptionList.Description>
                                          {lineItem.personalization.style}
                                        </DescriptionList.Description>
                                      </React.Fragment>
                                    )}
                                  </DescriptionList>
                              </Disclosure.Content>
                            </Disclosure>
                          )}
                        </Clipped>
                      </Level.Item>
                    </Level.Left>
                    {!isReadOnly && (
                      <Level.Right>
                        <Level.Item>
                          <Button
                            type="button"
                            icon="close"
                            color="light"
                            size="small"
                            isLoading={isRemovingLineItem === lineItem.id}
                            aria-label={`Remove ${lineItem.variant.name} from cart`}
                            onClick={() => removeLineItem(lineItem)}
                            disabled={isLoading}
                          />
                        </Level.Item>
                      </Level.Right>
                    )}
                  </Level>
                  <Box paddingTop={0.5}>
                    <Level>
                      <Level.Left>
                        <Level.Item>
                          <Text hasEllipsis isBold>
                            <Price value={lineItem.price} />
                          </Text>
                        </Level.Item>
                        {(!isReadOnly && lineItem.variant && !lineItem.variant.isSingleSalable) && (
                          <Level.Item>
                            <Select
                              type="inline"
                              removeHint
                              name="quantity"
                              label="Qty"
                              aria-label={`Quantity of ${lineItem.variant.name}`}
                              required
                              value={lineItem.quantity}
                              options={QUANTITY_OPTIONS}
                              onChange={({ target }) => updateQuantity(lineItem, target.value)}
                              isLoading={updatingQuantity === lineItem.id}
                              disabled={isLoading}
                            />
                          </Level.Item>
                        )}
                        {(isReadOnly && lineItem.variant && !lineItem.variant.isSingleSalable) && (
                          <Level.Item>
                            <Text hasEllipsis isBold><Text color="muted">Qty</Text> {lineItem.quantity}</Text>
                          </Level.Item>
                        )}
                      </Level.Left>
                    </Level>
                  </Box>
                </Media.Content>
              </Media>
            ))}
          </ContentFallback>
        </Drawer.Content>
      )}
      {!!(isReadOnly) && (
        <Drawer.Footer>
          <Box paddingVertical={0.5}>
            <Button
              type="button"
              icon="shoppingCart"
              isFullwidth
              disabled={isLoading}
              isLoading={isRedirecting}
              onClick={() => {
                setRedirecting(true)

                Analytics
                  .event({
                    category: 'Cart Summary',
                    action: 'Edit Cart Button Clicked',
                  }, {
                    isInteractive: true,
                    // We'll wait for the event to track before we actually redirect the user.
                    callback: () => {
                      Turbolinks.visit('/cart')
                    },
                  })
              }}
            >
              Edit Cart
            </Button>
          </Box>
          {!!(order && order.number) && (
            <Text hasEllipsis alignment="center" size="tiny" color="muted">
              Cart Number {order.number}
            </Text>
          )}
        </Drawer.Footer>
      )}
      {!!(!isReadOnly && order && order.lineItems.length) && (
        <Drawer.Footer>
          <Level>
            <Level.Right>
              <Level.Item>
                <Clipped>
                  <Text alignment="right" hasEllipsis>
                    Subtotal
                  </Text>
                  <Text alignment="right" size="large" isBold hasEllipsis hasInaccessibleLineHeight>
                    <Price value={order.total} />
                  </Text>
                </Clipped>
              </Level.Item>
            </Level.Right>
          </Level>
          <Box paddingVertical={0.5}>
            <Button
              type="button"
              color="action"
              icon="lock"
              isFullwidth
              disabled={isLoading}
              isLoading={isRedirecting}
              onClick={() => {
                setRedirecting(true)
                Analytics
                  .event({
                    category: 'Cart Summary',
                    action: 'Checkout Button Clicked',
                  }, {
                    isInteractive: true,
                    // We'll wait for the event to track before we actually redirect the user.
                    callback: () => {
                      Turbolinks.visit('/checkout')
                      onClose()
                    },
                  })
              }}
            >
              Proceed to Checkout
            </Button>
          </Box>
          <Text hasEllipsis alignment="center" size="tiny" color="muted">
            Cart Number {order.number}
          </Text>
        </Drawer.Footer>
      )}
    </Drawer>
  )
}

CartSidebar.propTypes = {
  orderNumber: PropTypes.string.isRequired,
  orderToken: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  popupProps: PropTypes.shape({
    isOpen: PropTypes.bool,
    role: PropTypes.string,
    id: PropTypes.string,
    "aria-modal": PropTypes.string,
    "aria-hidden": PropTypes.string,
    close: PropTypes.func,
  }),
  onUpdate: PropTypes.func,
  isReadOnly: PropTypes.bool,
}

CartSidebar.defaultProps = {
  isReadOnly: false,
  onUpdate: undefined,
}

export default CartSidebar
